import {
  ESdChangeReason,
  IAction,
  IDisplayItem,
  IProject,
  IStartProcessTriggerSourceType
} from "@kuika/kuika-cl-model";
import type { ScreenDesignContextProvider } from "../../contexts/screen-design-context/screen-design-context-provider";
import {
  addStartProcessTrigger,
  applyStartProcessTriggerDiff,
  collectStartProcessActionsInActionTree,
  collectStartProcessActionsInDisplayItemSubtree,
  ICollectedStartProcessAction,
  pruneOrphanStartProcessTriggers,
  removeAllStartProcessTriggersBySource,
  removeAllStartProcessTriggersByWorkflow,
  removeStartProcessTrigger
} from "./start-process-trigger-index-helper";

interface IApplyDiffParams {
  sourceType: IStartProcessTriggerSourceType;
  sourceId: string | undefined;
  previousSnapshot: ICollectedStartProcessAction[];
  currentSnapshot: ICollectedStartProcessAction[];
}

/**
 * High-level façade over start-process-trigger-index-helper primitives.
 *
 * Centralizes the "compute diff, call updateProject if changed" boilerplate
 * that would otherwise repeat at every hook site. Each call site becomes:
 *   - 1 line for entity-level prunes (Screen / ScheduledJob / Api / Workflow delete)
 *   - 2-3 lines for action-mutation hooks (snapshot before + applyDiff after)
 *
 * One instance lives on ScreenDesignContextProvider; access via:
 *   - `this.partial.startProcessTriggerSync` from provider partitions
 *   - `screenContext.startProcessTriggerSync` from designer helpers
 *   - `getSdInstance().startProcessTriggerSync` from UI components
 */
export class StartProcessTriggerSync {
  constructor(private readonly sd: ScreenDesignContextProvider) {}

  /** Capture a snapshot of StartProcess actions in an action tree. */
  snapshot = (actions: IAction[] | undefined): ICollectedStartProcessAction[] => {
    return collectStartProcessActionsInActionTree(actions, this.sd.state.actionDefinitions);
  };

  /**
   * Apply the diff for an action-mutation hook. Caller passes both snapshots
   * (StartProcess actions collected before and after its mutation). If the
   * resulting project differs, fires updateProject (autosave).
   */
  applyDiff = ({ sourceType, sourceId, previousSnapshot, currentSnapshot }: IApplyDiffParams): void => {
    if (!sourceId) return;
    const project = this.sd.state.project;
    if (!project) return;
    const nextProject = applyStartProcessTriggerDiff({
      project,
      sourceType,
      sourceId,
      previousSnapshot,
      currentSnapshot
    });
    this.commit(nextProject, project);
  };

  /** Component delete: scan the removed DisplayItem subtree for StartProcess
   *  actions and drop their index entries. */
  pruneRemovedDisplayItemSubtree = (di: IDisplayItem | undefined, screenId: string | undefined): void => {
    if (!di || !screenId) return;
    const project = this.sd.state.project;
    if (!project) return;
    const found = collectStartProcessActionsInDisplayItemSubtree(di, this.sd.state.actionDefinitions);
    if (found.length === 0) return;
    let nextProject = project;
    for (const item of found) {
      nextProject = removeStartProcessTrigger(nextProject, item.workflowId, "Screen", screenId, item.actionId);
    }
    this.commit(nextProject, project);
  };

  /** AI screen generation: scan the just-generated display item tree and add
   *  trigger entries for every StartProcess action found. */
  syncGeneratedScreen = (screenId: string | undefined, displayItems: IDisplayItem[] | undefined): void => {
    if (!screenId || !displayItems?.length) return;
    const project = this.sd.state.project;
    if (!project) return;
    let nextProject = project;
    for (const rootDi of displayItems) {
      const found = collectStartProcessActionsInDisplayItemSubtree(rootDi, this.sd.state.actionDefinitions);
      for (const item of found) {
        nextProject = addStartProcessTrigger(nextProject, item.workflowId, {
          sourceType: "Screen",
          sourceId: screenId,
          actionId: item.actionId
        });
      }
    }
    this.commit(nextProject, project);
  };

  /** Undo / redo restores a screen's displayItems wholesale via the redux history
   *  store, bypassing the per-action funnels (saveActionToEvent / removeItem). Reconcile
   *  this screen's StartProcess trigger entries to exactly match the restored tree. The
   *  displayItems walk is cheap (the actionDefinitions lookup runs only per action, and
   *  screens carry few); the network patch is gated on an actual set difference, so the
   *  common undo that doesn't touch a StartProcess fires nothing. */
  reconcileScreen = (screenId: string | undefined, displayItems: IDisplayItem[] | undefined): void => {
    if (!screenId) return;
    const project = this.sd.state.project;
    if (!project) return;

    // Desired: StartProcess actions present in the restored tree.
    const desired: ICollectedStartProcessAction[] = [];
    for (const rootDi of displayItems ?? []) {
      desired.push(...collectStartProcessActionsInDisplayItemSubtree(rootDi, this.sd.state.actionDefinitions));
    }

    // Current: index entries pointing at this screen (keyed by actionId|workflowId).
    const triggers = project.startProcessTriggers ?? {};
    const currentKeys = new Set<string>();
    for (const workflowId of Object.keys(triggers)) {
      for (const trigger of triggers[workflowId] ?? []) {
        if (trigger.sourceType === "Screen" && trigger.sourceId === screenId) {
          currentKeys.add(`${trigger.actionId}|${workflowId}`);
        }
      }
    }
    const desiredKeys = new Set<string>(desired.map((d) => `${d.actionId}|${d.workflowId}`));

    // No change → skip the patch entirely (most undos don't touch a StartProcess).
    if (desiredKeys.size === currentKeys.size && [...desiredKeys].every((key) => currentKeys.has(key))) {
      return;
    }

    // Rebuild this screen's entries: drop all, re-add the desired set, single commit.
    let nextProject = removeAllStartProcessTriggersBySource(project, "Screen", screenId);
    for (const item of desired) {
      nextProject = addStartProcessTrigger(nextProject, item.workflowId, {
        sourceType: "Screen",
        sourceId: screenId,
        actionId: item.actionId
      });
    }
    this.commit(nextProject, project);
  };

  pruneScreen = (screenId: string | undefined): void => this.pruneSource("Screen", screenId);

  pruneScheduledJob = (jobId: string | undefined): void => this.pruneSource("ScheduledJob", jobId);

  pruneApiManagement = (apiId: string | undefined): void => this.pruneSource("ApiManagement", apiId);

  pruneWorkflow = (workflowId: string | undefined): void => {
    if (!workflowId) return;
    const project = this.sd.state.project;
    if (!project) return;
    const nextProject = removeAllStartProcessTriggersByWorkflow(project, workflowId);
    this.commit(nextProject, project);
  };

  /**
   * Sweep the index for orphan entries (source entity no longer exists) and
   * commit a cleanup patch if any were found. Fire-and-forget; safe to call
   * proactively (no-op when there's nothing to clean).
   */
  pruneOrphans = (): void => {
    const project = this.sd.state.project;
    if (!project) return;
    const screenIds = new Set((this.sd.state.screenList ?? []).map((s) => s.id));
    const scheduledJobIds = new Set((this.sd.state.scheduledJobs ?? []).map((j) => j.id));
    const apiManagementIds = new Set((this.sd.state.apiManagements ?? []).map((a) => a.id));
    const nextProject = pruneOrphanStartProcessTriggers(project, screenIds, scheduledJobIds, apiManagementIds);
    this.commit(nextProject, project);
  };

  private pruneSource = (sourceType: IStartProcessTriggerSourceType, sourceId: string | undefined): void => {
    if (!sourceId) return;
    const project = this.sd.state.project;
    if (!project) return;
    const nextProject = removeAllStartProcessTriggersBySource(project, sourceType, sourceId);
    this.commit(nextProject, project);
  };

  private commit = (nextProject: IProject, project: IProject): void => {
    if (nextProject === project) return;
    // Mirror the new triggers synchronously onto the live state.project ref so any
    // load*** function that captures state.project right after this commit still sees
    // the updated triggers. React's setState in updateProject is batched and won't be
    // visible immediately, so the direct mutation is what closes the captured-ref race.
    if (this.sd.state.project) {
      this.sd.state.project.startProcessTriggers = nextProject.startProcessTriggers;
    }
    void this.sd.projectProvider.updateProject(nextProject, true, ESdChangeReason.OtherSetStates);
  };
}
